import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Users,
  TrendingUp,
  DollarSign,
  Activity,
  RefreshCw,
  AlertTriangle,
  Search,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  Flame
} from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const USERS_PAGE_LIMIT = 25;
const ACTIVITY_LIMIT = 25;

const AdminStandaloneMonitor = () => {
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();
  const { t } = useTranslation();

  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(null);

  const [engagement, setEngagement] = useState(null);
  const [engagementLoading, setEngagementLoading] = useState(true);
  const [engagementError, setEngagementError] = useState(null);

  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState(null);

  const [usersData, setUsersData] = useState({ users: [], total: 0, count: 0, page: 1, limit: USERS_PAGE_LIMIT });
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    plan: 'all',
    status: 'all',
    autoRenew: 'all',
    paymentType: 'all',
    activity: 'all',
    sort: 'recent_activity_desc',
    search: ''
  });

  const [atRisk, setAtRisk] = useState([]);
  const [atRiskLoading, setAtRiskLoading] = useState(true);
  const [atRiskError, setAtRiskError] = useState(null);

  const [topUsers, setTopUsers] = useState([]);
  const [topUsersLoading, setTopUsersLoading] = useState(true);
  const [topUsersError, setTopUsersError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');

  const formatNumber = useCallback((value, options = {}) => {
    if (value === null || value === undefined) return '-';
    const formatter = new Intl.NumberFormat('it-IT', {
      maximumFractionDigits: options.maxDecimals ?? 0,
      minimumFractionDigits: options.minDecimals ?? 0
    });
    return formatter.format(value);
  }, []);

  const formatCurrency = useCallback((value) => {
    if (value === null || value === undefined) return '€0';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 2
    }).format(value);
  }, []);

  const formatDateTime = useCallback((value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  }, []);

  const fetchOverview = useCallback(async () => {
    if (!api) {
      setOverviewLoading(false);
      setOverviewError(t('admin.standaloneMonitor.errors.noApi'));
      return;
    }
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const response = await api.get('/admin_standalone_dashboard.php', {
        params: { action: 'overview' }
      });
      const raw = response.data?.data ?? response.data;
      setOverview(raw && (raw.totals || raw.plans) ? raw : null);
    } catch (error) {
      console.error('StandaloneMonitor overview error:', error);
      const msg =
        error.response?.data?.error ||
        error.message ||
        (error.response?.status === 404 ? 'Endpoint non trovato (404). Verifica che admin_standalone_dashboard.php sia sul server.' : null) ||
        t('admin.standaloneMonitor.errors.overview');
      setOverviewError(msg);
    } finally {
      setOverviewLoading(false);
    }
  }, [api, t]);

  const fetchEngagement = useCallback(async () => {
    if (!api) return;
    setEngagementLoading(true);
    setEngagementError(null);
    try {
      const response = await api.get('/admin_standalone_dashboard.php', {
        params: {
          action: 'engagement',
          period: 30
        }
      });
      setEngagement(response.data?.data ?? null);
    } catch (error) {
      console.error('StandaloneMonitor engagement error:', error);
      setEngagementError(t('admin.standaloneMonitor.errors.engagement'));
    } finally {
      setEngagementLoading(false);
    }
  }, [api, t]);

  const fetchActivity = useCallback(async () => {
    if (!api) return;
    setActivityLoading(true);
    setActivityError(null);
    try {
      const response = await api.get('/admin_standalone_dashboard.php', {
        params: {
          action: 'activity_feed',
          limit: ACTIVITY_LIMIT,
          page: 1
        }
      });
      setActivity(response.data?.data?.events ?? []);
    } catch (error) {
      console.error('StandaloneMonitor activity error:', error);
      setActivityError(t('admin.standaloneMonitor.errors.activity'));
    } finally {
      setActivityLoading(false);
    }
  }, [api, t]);

  const fetchAtRisk = useCallback(async () => {
    if (!api) return;
    setAtRiskLoading(true);
    setAtRiskError(null);
    try {
      const response = await api.get('/admin_standalone_dashboard.php', {
        params: {
          action: 'at_risk',
          days: 30,
          limit: 8
        }
      });
      setAtRisk(response.data?.data ?? []);
    } catch (error) {
      console.error('StandaloneMonitor atRisk error:', error);
      setAtRiskError(t('admin.standaloneMonitor.errors.atRisk'));
    } finally {
      setAtRiskLoading(false);
    }
  }, [api, t]);

  const fetchTopUsers = useCallback(async () => {
    if (!api) return;
    setTopUsersLoading(true);
    setTopUsersError(null);
    try {
      const response = await api.get('/admin_standalone_dashboard.php', {
        params: {
          action: 'top_users',
          metric: 'workouts',
          period: 30,
          limit: 6
        }
      });
      setTopUsers(response.data?.data ?? []);
    } catch (error) {
      console.error('StandaloneMonitor topUsers error:', error);
      setTopUsersError(t('admin.standaloneMonitor.errors.topUsers'));
    } finally {
      setTopUsersLoading(false);
    }
  }, [api, t]);

  const fetchUsers = useCallback(
    async (withLoading = true) => {
      if (!api) return;
      if (withLoading) setUsersLoading(true);
      setUsersError(null);
      try {
        const response = await api.get('/admin_standalone_dashboard.php', {
          params: {
            action: 'users',
            plan: filters.plan,
            status: filters.status,
            auto_renew: filters.autoRenew,
            payment_type: filters.paymentType,
            activity: filters.activity,
            sort: filters.sort,
            search: filters.search,
            limit: USERS_PAGE_LIMIT,
            page
          }
        });
        setUsersData(response.data?.data ?? { users: [], total: 0, count: 0, page, limit: USERS_PAGE_LIMIT });
      } catch (error) {
        console.error('StandaloneMonitor users error:', error);
        setUsersError(t('admin.standaloneMonitor.errors.users'));
      } finally {
        if (withLoading) setUsersLoading(false);
      }
    },
    [api, filters, page, t]
  );

  useEffect(() => {
    fetchOverview();
    fetchEngagement();
    fetchActivity();
    fetchAtRisk();
    fetchTopUsers();
  }, [fetchOverview, fetchEngagement, fetchActivity, fetchAtRisk, fetchTopUsers]);

  useEffect(() => {
    fetchUsers(true);
  }, [fetchUsers]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm.trim() }));
      setPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleManualRefresh = () => {
    fetchOverview();
    fetchEngagement();
    fetchActivity();
    fetchAtRisk();
    fetchTopUsers();
    fetchUsers(false);
  };

  const usersMaxPage = useMemo(() => Math.max(1, Math.ceil((usersData.total ?? 0) / USERS_PAGE_LIMIT)), [usersData.total]);

  const overviewCards = useMemo(() => {
    if (!overview?.totals) return [];
    const totals = overview.totals;
    return [
      {
        key: 'totalUsers',
        icon: Users,
        label: t('admin.standaloneMonitor.totals.totalUsers'),
        value: formatNumber(totals.users)
      },
      {
        key: 'premiumUsers',
        icon: CreditCard,
        label: t('admin.standaloneMonitor.totals.premiumUsers'),
        value: formatNumber(totals.premium)
      },
      {
        key: 'conversionRate',
        icon: TrendingUp,
        label: t('admin.standaloneMonitor.totals.conversionRate'),
        value: `${formatNumber(totals.conversion_rate, { maxDecimals: 1 })}%`
      },
      {
        key: 'mrr',
        icon: DollarSign,
        label: t('admin.standaloneMonitor.totals.mrrEstimate'),
        value: formatCurrency(totals.mrr_estimate)
      },
      {
        key: 'newUsers',
        icon: Users,
        label: t('admin.standaloneMonitor.totals.newUsers30d'),
        value: formatNumber(totals.new_users_30d)
      },
      {
        key: 'activeWorkout',
        icon: Activity,
        label: t('admin.standaloneMonitor.totals.activeWorkout30d'),
        value: formatNumber(totals.active_workout_30d)
      }
    ];
  }, [overview, formatCurrency, formatNumber, t]);

  const tabTriggerClass =
    'group relative flex items-center gap-2 rounded-xl border border-transparent px-4 py-2 text-sm font-medium text-gray-500 transition-all hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 data-[state=active]:border-blue-500/70 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg whitespace-nowrap flex-shrink-0';
  const formatActivityMetric = useCallback(
    (event) => {
      if (event?.metric_value === null || event?.metric_value === undefined) return null;
      if (event.type === 'upgrade' || event.type === 'downgrade') {
        return formatCurrency(event.metric_value);
      }
      if (event.type === 'workout') {
        const minutes = Math.round(event.metric_value ?? 0);
        return t('admin.standaloneMonitor.activity.metric.workoutDuration', {
          minutes: formatNumber(minutes)
        });
      }
      return null;
    },
    [formatCurrency, formatNumber, t]
  );

  const renderOverviewSection = () => (
    <section className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow min-h-[320px]">
      {overviewLoading ? (
        <div className="flex justify-center items-center py-14 min-h-[200px]">
          <LoadingSpinner size="lg" />
        </div>
      ) : overviewError ? (
        <div className="flex items-center gap-3 text-red-600 dark:text-red-400 p-6 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle className="h-6 w-6 flex-shrink-0" />
          <span className="text-sm">{overviewError}</span>
        </div>
      ) : overview && (overview.totals || overview.plans) ? (
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {overviewCards.map((card) => (
              <div
                key={card.key}
                className="bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 rounded-xl p-4 flex items-center gap-3"
              >
                <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
                  <card.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">{card.label}</p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">{card.value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t('admin.standaloneMonitor.plans.title')}
                </h3>
              </div>
              <ul className="space-y-3">
                {overview.plans?.map((plan) => (
                  <li key={`${plan.plan_id ?? 'free'}`} className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {plan.name || t('admin.standaloneMonitor.plans.free')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {plan.price > 0 ? formatCurrency(plan.price) : t('admin.standaloneMonitor.plans.freeLabel')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-gray-100">{formatNumber(plan.total_users)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('admin.standaloneMonitor.plans.active30d', { count: formatNumber(plan.active_last_30_days) })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t('admin.standaloneMonitor.totals.upgradeSummary')}
                </h3>
              </div>
              <dl className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <div className="flex justify-between">
                  <dt>{t('admin.standaloneMonitor.totals.newUsers7d')}</dt>
                  <dd className="font-semibold">{formatNumber(overview.totals?.new_users_7d)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>{t('admin.standaloneMonitor.totals.upgrades30d')}</dt>
                  <dd className="font-semibold">{formatNumber(overview.totals?.upgrades_30d)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>{t('admin.standaloneMonitor.totals.autoRenew')}</dt>
                  <dd className="font-semibold">{formatNumber(overview.totals?.auto_renew_premium)}</dd>
                </div>
              </dl>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t('admin.standaloneMonitor.engagement.title')}
                </h3>
              </div>
              {engagementLoading ? (
                <div className="flex justify-center py-6">
                  <LoadingSpinner />
                </div>
              ) : engagementError ? (
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  <span>{engagementError}</span>
                </div>
              ) : engagement ? (
                <dl className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <div className="flex justify-between">
                    <dt>{t('admin.standaloneMonitor.engagement.workouts')}</dt>
                    <dd className="font-semibold">{formatNumber(engagement.workouts?.total)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>{t('admin.standaloneMonitor.engagement.activeUsers')}</dt>
                    <dd className="font-semibold">{formatNumber(engagement.workouts?.active_users)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>{t('admin.standaloneMonitor.engagement.avgPerUser')}</dt>
                    <dd className="font-semibold">
                      {formatNumber(engagement.workouts?.avg_workouts_per_user, { maxDecimals: 2 })}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>{t('admin.standaloneMonitor.engagement.createdWorkouts')}</dt>
                    <dd className="font-semibold">{formatNumber(engagement.created_workouts)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>{t('admin.standaloneMonitor.engagement.customExercises')}</dt>
                    <dd className="font-semibold">{formatNumber(engagement.custom_exercises)}</dd>
                  </div>
                </dl>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">-</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="p-8 text-center rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 min-h-[200px] flex flex-col items-center justify-center">
          <AlertCircle className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
          <p className="text-gray-700 dark:text-gray-300 font-medium mb-2">
            {t('admin.standaloneMonitor.overviewEmpty')}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
            {t('admin.standaloneMonitor.overviewEmptyHint')}
          </p>
        </div>
      )}
    </section>
  );

  const renderActivitySection = () => (
    <section className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow min-h-[320px]">
      <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('admin.standaloneMonitor.activity.title')}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => fetchActivity()}
          className="inline-flex items-center px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" />
          {t('common.refresh')}
        </button>
      </div>
      <div className="min-h-[200px] overflow-y-auto">
        {activityLoading ? (
          <div className="flex justify-center items-center py-14 min-h-[200px]">
            <LoadingSpinner />
          </div>
        ) : activityError ? (
          <div className="flex items-center gap-3 text-red-600 dark:text-red-400 p-6 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <AlertTriangle className="h-6 w-6 flex-shrink-0" />
            <span className="text-sm">{activityError}</span>
          </div>
        ) : activity.length === 0 ? (
          <div className="p-8 text-center rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 min-h-[180px] flex flex-col items-center justify-center">
            <Activity className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
            <p className="text-gray-700 dark:text-gray-300 font-medium">
              {t('admin.standaloneMonitor.activity.empty')}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
            {activity.map((event, idx) => (
              <li key={`${event.type}-${event.user_id ?? event.event_id ?? idx}-${event.created_at ?? event.timestamp}`} className="p-6 flex flex-col gap-1">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {t(`admin.standaloneMonitor.activity.types.${event.type}`, event.type)}
                    </p>
                    <p className="text-gray-600 dark:text-gray-300">
                      {event.actor_name ?? event.user_name ?? '-'}{' '}
                      <span className="text-xs text-gray-400">{event.actor_email ?? event.email ?? ''}</span>
                    </p>
                  </div>
                  <span className="text-xs text-gray-400">{formatDateTime(event.timestamp ?? event.created_at)}</span>
                </div>
                {event.plan_name && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <CreditCard className="h-3 w-3" />
                    {event.plan_name}
                  </p>
                )}
                {formatActivityMetric(event) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{formatActivityMetric(event)}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );

  const renderUsersSection = () => (
    <section className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow min-h-[320px]">
      <div className="p-6 border-b border-gray-100 dark:border-gray-800 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('admin.standaloneMonitor.users.title')}
          </h3>
          <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-2 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-1 bg-gray-50 dark:bg-gray-800/40">
              <span className="font-medium text-gray-700 dark:text-gray-200">
                {formatNumber(usersData.total ?? 0)}
              </span>
              <span>{t('admin.standaloneMonitor.users.total')}</span>
            </div>
            <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-4 w-4" />
              <span>{t('admin.standaloneMonitor.users.activePage', { count: formatNumber(usersData.count ?? 0) })}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 left-3 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('admin.standaloneMonitor.users.searchPlaceholder')}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              {t('admin.standaloneMonitor.filters.plan')}
              <select
                className="rounded-md border border-gray-200 dark:border-gray-700 bg-transparent text-sm text-gray-700 dark:text-gray-100"
                value={filters.plan}
                onChange={(event) => handleFilterChange('plan', event.target.value)}
              >
                <option value="all">{t('admin.standaloneMonitor.filters.planAll')}</option>
                <option value="free">{t('admin.standaloneMonitor.filters.planFree')}</option>
                <option value="premium">{t('admin.standaloneMonitor.filters.planPremium')}</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              {t('admin.standaloneMonitor.filters.status')}
              <select
                className="rounded-md border border-gray-200 dark:border-gray-700 bg-transparent text-sm text-gray-700 dark:text-gray-100"
                value={filters.status}
                onChange={(event) => handleFilterChange('status', event.target.value)}
              >
                <option value="all">{t('admin.standaloneMonitor.filters.statusAll')}</option>
                <option value="active">{t('admin.standaloneMonitor.filters.statusActive')}</option>
                <option value="expired">{t('admin.standaloneMonitor.filters.statusExpired')}</option>
                <option value="cancelled">{t('admin.standaloneMonitor.filters.statusCancelled')}</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              {t('admin.standaloneMonitor.filters.autoRenew')}
              <select
                className="rounded-md border border-gray-200 dark:border-gray-700 bg-transparent text-sm text-gray-700 dark:text-gray-100"
                value={filters.autoRenew}
                onChange={(event) => handleFilterChange('autoRenew', event.target.value)}
              >
                <option value="all">{t('admin.standaloneMonitor.filters.autoRenewAll')}</option>
                <option value="on">{t('admin.standaloneMonitor.filters.autoRenewOn')}</option>
                <option value="off">{t('admin.standaloneMonitor.filters.autoRenewOff')}</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              {t('admin.standaloneMonitor.filters.paymentType')}
              <select
                className="rounded-md border border-gray-200 dark:border-gray-700 bg-transparent text-sm text-gray-700 dark:text-gray-100"
                value={filters.paymentType}
                onChange={(event) => handleFilterChange('paymentType', event.target.value)}
              >
                <option value="all">{t('admin.standaloneMonitor.filters.paymentAll')}</option>
                <option value="recurring">{t('admin.standaloneMonitor.filters.paymentRecurring')}</option>
                <option value="onetime">{t('admin.standaloneMonitor.filters.paymentOneTime')}</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              {t('admin.standaloneMonitor.filters.activity')}
              <select
                className="rounded-md border border-gray-200 dark:border-gray-700 bg-transparent text-sm text-gray-700 dark:text-gray-100"
                value={filters.activity}
                onChange={(event) => handleFilterChange('activity', event.target.value)}
              >
                <option value="all">{t('admin.standaloneMonitor.filters.activityAll')}</option>
                <option value="active_30d">{t('admin.standaloneMonitor.filters.activityActive')}</option>
                <option value="inactive_30d">{t('admin.standaloneMonitor.filters.activityInactive')}</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto min-h-[200px]">
        {usersLoading ? (
          <div className="flex justify-center items-center py-14 min-h-[200px]">
            <LoadingSpinner />
          </div>
        ) : usersError ? (
          <div className="flex items-center gap-3 text-red-600 dark:text-red-400 p-6 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <AlertTriangle className="h-6 w-6 flex-shrink-0" />
            <span className="text-sm">{usersError}</span>
          </div>
        ) : usersData.users.length === 0 ? (
          <div className="p-8 text-center rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 min-h-[180px] flex flex-col items-center justify-center">
            <Users className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
            <p className="text-gray-700 dark:text-gray-300 font-medium">
              {t('admin.standaloneMonitor.users.empty')}
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800 table-fixed">
            <thead className="bg-gray-50 dark:bg-gray-800/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-[20%]">
                  {t('admin.standaloneMonitor.users.columns.name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-[20%]">
                  {t('admin.standaloneMonitor.users.columns.email')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase w-[14%]">
                  {t('admin.standaloneMonitor.users.columns.plan')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase w-[14%]">
                  {t('admin.standaloneMonitor.users.columns.subscription')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase w-[14%]">
                  {t('admin.standaloneMonitor.users.columns.payment')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase w-[14%]">
                  {t('admin.standaloneMonitor.users.columns.lastActivity')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase text-right w-[8%]">
                  {t('admin.standaloneMonitor.users.columns.workouts')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800 text-sm">
              {usersData.users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4">
                    <p className="font-medium text-gray-900 dark:text-gray-100">{user.name || '-'}</p>
                    <p className="text-xs text-gray-400">{user.username}</p>
                  </td>
                  <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{user.email || '-'}</td>
                  <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {user.plan?.name || t('admin.standaloneMonitor.plans.free')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {user.plan?.price ? formatCurrency(user.plan?.price) : t('admin.standaloneMonitor.plans.freeLabel')}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                    <p className="flex items-center gap-1">
                      {user.subscription?.status === 'active' ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-amber-500" />
                      )}
                      <span className="uppercase text-xs">{user.subscription?.status || '-'}</span>
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {user.subscription?.start_date ? formatDateTime(user.subscription?.start_date) : '-'}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                    <p className="capitalize">{user.subscription?.payment_type || '-'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {user.subscription?.auto_renew
                        ? t('admin.standaloneMonitor.users.autoRenewOn')
                        : t('admin.standaloneMonitor.users.autoRenewOff')}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                    <p className="text-sm text-gray-900 dark:text-gray-100">{formatDateTime(user.last_activity)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatDateTime(user.last_workout_date)}</p>
                  </td>
                  <td className="px-6 py-4 text-right text-gray-600 dark:text-gray-300">
                    <p className="font-semibold">{formatNumber(user.workouts?.last_30_days)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatNumber(user.workouts?.total)}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-800 text-sm text-gray-600 dark:text-gray-300">
        <span>
          {usersData.total > 0 &&
            `${(page - 1) * USERS_PAGE_LIMIT + 1}-${Math.min(page * USERS_PAGE_LIMIT, usersData.total)} / ${usersData.total}`}
        </span>
        <div className="flex items-center gap-2">
          <select
            value={filters.sort}
            onChange={(event) => handleFilterChange('sort', event.target.value)}
            className="rounded-md border border-gray-200 dark:border-gray-700 bg-transparent text-sm text-gray-700 dark:text-gray-100"
          >
            <option value="recent_activity_desc">{t('admin.standaloneMonitor.sort.recentDesc')}</option>
            <option value="recent_activity_asc">{t('admin.standaloneMonitor.sort.recentAsc')}</option>
            <option value="created_desc">{t('admin.standaloneMonitor.sort.createdDesc')}</option>
            <option value="created_asc">{t('admin.standaloneMonitor.sort.createdAsc')}</option>
            <option value="plan_price_desc">{t('admin.standaloneMonitor.sort.priceDesc')}</option>
            <option value="plan_price_asc">{t('admin.standaloneMonitor.sort.priceAsc')}</option>
          </select>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 text-sm disabled:opacity-40"
            >
              ‹
            </button>
            <span>
              {page}/{usersMaxPage}
            </span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(usersMaxPage, prev + 1))}
              disabled={page >= usersMaxPage}
              className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 text-sm disabled:opacity-40"
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </section>
  );

  const renderAtRiskSection = () => (
    <section className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow min-h-[320px]">
      <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('admin.standaloneMonitor.atRisk.title')}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => fetchAtRisk()}
          className="inline-flex items-center px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" />
          {t('common.refresh')}
        </button>
      </div>
      <div className="min-h-[200px] overflow-y-auto">
        {atRiskLoading ? (
          <div className="flex justify-center items-center py-14 min-h-[200px]">
            <LoadingSpinner />
          </div>
        ) : atRiskError ? (
          <div className="flex items-center gap-3 text-red-600 dark:text-red-400 p-6 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <AlertTriangle className="h-6 w-6 flex-shrink-0" />
            <span className="text-sm">{atRiskError}</span>
          </div>
        ) : atRisk.length === 0 ? (
          <div className="p-8 text-center rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 min-h-[180px] flex flex-col items-center justify-center">
            <AlertTriangle className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
            <p className="text-gray-700 dark:text-gray-300 font-medium">
              {t('admin.standaloneMonitor.atRisk.empty')}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
            {atRisk.map((user) => {
              const id = user.id ?? user.user_id;
              const lastDate = user.last_workout_date ?? user.last_activity;
              const daysInactive = user.days_inactive ?? (lastDate ? Math.max(0, Math.floor((new Date() - new Date(lastDate)) / 86400000)) : null);
              return (
                <li key={id} className="p-6 flex justify-between gap-4">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{user.name || '-'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{user.email ?? ''}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('admin.standaloneMonitor.atRisk.planLabel', {
                        plan: user.plan || t('admin.standaloneMonitor.plans.free')
                      })}
                    </p>
                  </div>
                  <div className="text-right text-gray-700 dark:text-gray-200">
                    <p className="font-semibold">
                      {t('admin.standaloneMonitor.atRisk.days', { days: daysInactive ?? '?' })}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {lastDate ? formatDateTime(lastDate) : t('admin.standaloneMonitor.atRisk.never')}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );

  const renderTopUsersSection = () => (
    <section className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow min-h-[320px]">
      <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <Flame className="h-5 w-5 text-emerald-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('admin.standaloneMonitor.topUsers.title')}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => fetchTopUsers()}
          className="inline-flex items-center px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" />
          {t('common.refresh')}
        </button>
      </div>
      <div className="min-h-[200px] overflow-y-auto">
        {topUsersLoading ? (
          <div className="flex justify-center items-center py-14 min-h-[200px]">
            <LoadingSpinner />
          </div>
        ) : topUsersError ? (
          <div className="flex items-center gap-3 text-red-600 dark:text-red-400 p-6 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <AlertTriangle className="h-6 w-6 flex-shrink-0" />
            <span className="text-sm">{topUsersError}</span>
          </div>
        ) : topUsers.length === 0 ? (
          <div className="p-8 text-center rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 min-h-[180px] flex flex-col items-center justify-center">
            <Flame className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
            <p className="text-gray-700 dark:text-gray-300 font-medium">
              {t('admin.standaloneMonitor.topUsers.empty')}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
            {topUsers.map((user, index) => {
              const id = user.id ?? user.user_id;
              const count = user.metric_value ?? user.workouts_count ?? 0;
              const total = user.workouts_count ?? count;
              return (
                <li key={id} className="p-6 flex justify-between gap-4">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      #{index + 1} {user.name || '-'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{user.email ?? ''}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('admin.standaloneMonitor.topUsers.plan', {
                        plan: user.plan || t('admin.standaloneMonitor.plans.free')
                      })}
                    </p>
                  </div>
                  <div className="text-right text-gray-700 dark:text-gray-200">
                    <p className="font-semibold">
                      {t('admin.standaloneMonitor.topUsers.workouts', {
                        count: formatNumber(count)
                      })}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('admin.standaloneMonitor.topUsers.total', {
                        total: formatNumber(total)
                      })}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 flex-shrink-0 mb-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{t('admin.standaloneMonitor.title')}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.standaloneMonitor.description')}</p>
        </div>
        <button
          type="button"
          onClick={handleManualRefresh}
          className="inline-flex items-center px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium shadow hover:bg-blue-700 transition"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('common.refresh')}
        </button>
      </div>

      <Tabs value={activeSection} onValueChange={setActiveSection} className="flex-1 min-h-[calc(100vh-14rem)] grid grid-rows-[auto_1fr] overflow-hidden">
        <div className="row-start-1 w-full overflow-x-auto overflow-y-hidden mb-4 scroll-smooth rounded-2xl border border-gray-200 bg-white/70 dark:border-gray-800 dark:bg-gray-900/70 shadow-inner">
          <TabsList className="flex flex-nowrap gap-2 p-2 h-auto bg-transparent border-0 shadow-none rounded-2xl min-w-max">
          <TabsTrigger value="overview" className={tabTriggerClass}>
            <TrendingUp className="h-4 w-4" />
            <span>{t('admin.standaloneMonitor.sections.overview')}</span>
          </TabsTrigger>
          <TabsTrigger value="activity" className={tabTriggerClass}>
            <Activity className="h-4 w-4" />
            <span>{t('admin.standaloneMonitor.sections.activity')}</span>
          </TabsTrigger>
          <TabsTrigger value="users" className={tabTriggerClass}>
            <Users className="h-4 w-4" />
            <span>{t('admin.standaloneMonitor.sections.users')}</span>
          </TabsTrigger>
          <TabsTrigger value="atRisk" className={tabTriggerClass}>
            <AlertTriangle className="h-4 w-4" />
            <span>{t('admin.standaloneMonitor.sections.atRisk')}</span>
          </TabsTrigger>
          <TabsTrigger value="top" className={tabTriggerClass}>
            <Flame className="h-4 w-4" />
            <span>{t('admin.standaloneMonitor.sections.topUsers')}</span>
          </TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="overview" className="row-start-2 min-h-[60vh] overflow-hidden focus:outline-none data-[state=inactive]:hidden mt-0 relative">
          <div className="absolute inset-0 overflow-y-auto overflow-x-hidden pr-1 min-h-[60vh]" style={{ overscrollBehavior: 'contain' }}>
            {renderOverviewSection()}
          </div>
        </TabsContent>

        <TabsContent value="activity" className="row-start-2 min-h-[60vh] overflow-hidden focus:outline-none data-[state=inactive]:hidden mt-0 relative">
          <div className="absolute inset-0 overflow-y-auto overflow-x-hidden pr-1 min-h-[60vh]" style={{ overscrollBehavior: 'contain' }}>
            {renderActivitySection()}
          </div>
        </TabsContent>

        <TabsContent value="users" className="row-start-2 min-h-[60vh] overflow-hidden focus:outline-none data-[state=inactive]:hidden mt-0 relative">
          <div className="absolute inset-0 overflow-y-auto overflow-x-hidden pr-1 min-h-[60vh]" style={{ overscrollBehavior: 'contain' }}>
            {renderUsersSection()}
          </div>
        </TabsContent>

        <TabsContent value="atRisk" className="row-start-2 min-h-[60vh] overflow-hidden focus:outline-none data-[state=inactive]:hidden mt-0 relative">
          <div className="absolute inset-0 overflow-y-auto overflow-x-hidden pr-1 min-h-[60vh]" style={{ overscrollBehavior: 'contain' }}>
            {renderAtRiskSection()}
          </div>
        </TabsContent>

        <TabsContent value="top" className="row-start-2 min-h-[60vh] overflow-hidden focus:outline-none data-[state=inactive]:hidden mt-0 relative">
          <div className="absolute inset-0 overflow-y-auto overflow-x-hidden pr-1 min-h-[60vh]" style={{ overscrollBehavior: 'contain' }}>
            {renderTopUsersSection()}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminStandaloneMonitor;

